import React, { useEffect, useMemo } from "react";
import { Stack, Box } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import FormSearchSelect from "../components/common/FormSearchSelect";
import SubmitButton from "../components/buttons/SubmitButton";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  createExpenseCategory,
  updateExpenseCategory,
} from "../redux/actions/expenseCategoryActions";
import { getAccounts } from "../redux/actions/accountActions";
import { cleanFormData } from "../helpers/formHelpers";
import { expenseCategoryActions } from "../redux/slices/expenseCategory";

import { ExpenseCategoryRequestDto } from "../shared/dtos/expenseCategory/ExpenseCategoryRequestDto";
import { ExpenseCategoryResponseDto } from "../shared/dtos/expenseCategory/ExpenseCategoryResponseDto";

const expenseCategorySchema = Yup.object().shape({
  name: Yup.string().required("Category name is required"),
  accountId: Yup.string().required("Linked expense account is required"),
  description: Yup.string().nullable(),
});

interface CreateOrUpdateExpenseCategoryFormProps {
  category?: ExpenseCategoryResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const CreateOrUpdateExpenseCategoryForm: React.FC<
  CreateOrUpdateExpenseCategoryFormProps
> = ({ category = null, isEditing = false, loading = false, error = null }) => {
  const dispatch = useAppDispatch();

  const { items: accounts, loadingFetchAll: loadingAccounts } = useAppSelector(
    (state) => state.account,
  );

  useEffect(() => {
    if (!accounts || accounts.length === 0) {
      dispatch(getAccounts({ page: 1, size: 200 }));
    }
  }, [dispatch, accounts]);

  const accountOptions = useMemo(
    () =>
      accounts?.map((acc) => ({
        value: acc.id,
        label: `${acc.name} (${acc.accountCode})`,
      })) || [],
    [accounts],
  );

  const initialValues: Partial<ExpenseCategoryRequestDto> = {
    name: category?.name || "",
    accountId: category?.accountId || "",
    description: category?.description || "",
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(
            isEditing
              ? expenseCategoryActions.setErrorUpdate(null)
              : expenseCategoryActions.setErrorCreate(null),
          )
        }
      />
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={expenseCategorySchema}
        onSubmit={(values) => {
          const payload = cleanFormData(
            values,
          ) as unknown as ExpenseCategoryRequestDto;
          if (isEditing && category) {
            dispatch(
              updateExpenseCategory({ id: category.id, values: payload }),
            );
          } else {
            dispatch(createExpenseCategory(payload));
          }
        }}
      >
        {({
          dirty,
          isValid,
        }: FormikProps<Partial<ExpenseCategoryRequestDto>>) => (
          <Form>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                }}
              >
                <FormTextField
                  name="name"
                  label="Category Name"
                  placeholder="e.g. Office Supplies"
                />
                <FormSearchSelect
                  name="accountId"
                  label="Linked Financial Account"
                  options={accountOptions}
                  loading={loadingAccounts}
                />
              </Box>

              <FormTextField
                name="description"
                label="Description"
                placeholder="Describe what expenses fall under this category..."
                multiline
                rows={3}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Expense Category"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateExpenseCategoryForm;
